/**
 * Business Type Step
 *
 * First step of the intake wizard - collects business entity information.
 */

import BankIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import TrustIcon from '@mui/icons-material/Gavel';
import PartnershipIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import { Box, GlassCard, Grid, MagicInput, MagicSelect, Typography, useI18n } from '@ops-matrix/design-system-ui';
import type { IntakeFormData } from '../IntakeWizard';

interface BusinessTypeStepProps {
  formData: IntakeFormData;
  updateFormData: (updates: Partial<IntakeFormData>) => void;
}

// Business type keys for i18n lookup
const BUSINESS_TYPE_KEYS = [
  {
    value: 'individual',
    labelKey: 'onboarding.businessEntity.individual',
    descKey: 'onboarding.businessEntity.individual.desc',
    icon: <PersonIcon />,
  },
  {
    value: 'llc',
    labelKey: 'onboarding.businessEntity.llc',
    descKey: 'onboarding.businessEntity.llc.desc',
    icon: <BusinessIcon />,
  },
  {
    value: 'corporation',
    labelKey: 'onboarding.businessEntity.corporation',
    descKey: 'onboarding.businessEntity.corporation.desc',
    icon: <BankIcon />,
  },
  {
    value: 'trust',
    labelKey: 'onboarding.businessEntity.trust',
    descKey: 'onboarding.businessEntity.trust.desc',
    icon: <TrustIcon />,
  },
  {
    value: 'partnership',
    labelKey: 'onboarding.businessEntity.partnership',
    descKey: 'onboarding.businessEntity.partnership.desc',
    icon: <PartnershipIcon />,
  },
] as const;

const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

export default function BusinessTypeStep({ formData, updateFormData }: BusinessTypeStepProps) {
  const { t } = useI18n();
  const showEntityDetails = formData.businessType && formData.businessType !== 'individual';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('onboarding.businessEntity.title')}
      </Typography>
      <Typography color="textSecondary" sx={{ mb: 3 }}>
        {t('onboarding.businessEntity.description')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {BUSINESS_TYPE_KEYS.map((type) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={type.value}>
            <GlassCard
              onClick={() => updateFormData({ businessType: type.value as IntakeFormData['businessType'] })}
              sx={{
                cursor: 'pointer',
                border: formData.businessType === type.value ? '2px solid' : '1px solid transparent',
                borderColor: formData.businessType === type.value ? 'primary.main' : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.light',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {type.icon}
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t(type.labelKey)}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {t(type.descKey)}
              </Typography>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      {showEntityDetails && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            {t('onboarding.entityDetails')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                label={t('employment.businessName')}
                value={formData.businessName}
                onChange={(e) => updateFormData({ businessName: e.target.value })}
                placeholder={t('onboarding.businessNamePlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicSelect
                fullWidth
                label={t('onboarding.stateOfFormation')}
                value={formData.stateOfFormation}
                onChange={(e) => updateFormData({ stateOfFormation: e.target.value as string })}
                options={[
                  { value: '', label: t('onboarding.selectState') },
                  ...US_STATES.map((state) => ({ value: state, label: state })),
                ]}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <MagicInput
                fullWidth
                label={t('employment.yearsInBusiness')}
                type="number"
                value={formData.yearsInBusiness}
                onChange={(e) => updateFormData({ yearsInBusiness: e.target.value })}
                placeholder={t('onboarding.yearsInBusinessPlaceholder')}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}
